import { keyMap, replaceKeys } from "./teamMetadataTransformers";

export type TeamsJsonInput = {
  version?: number;
  teams: Record<string, Record<string, TeamRawData>>;
};

export type TeamRawData = {
  name: string;
  shortName?: string;
  city?: string;
  nickName?: string;
  abbreviation?: string;
  alternateNames?: string[];
  metadata?: Record<string, unknown>;
  cid?: string;
};

export type TeamCompressedData = {
  n: string;
  sn?: string;
  c?: string;
  nn?: string;
  a?: string;
  l: string;
  m?: Record<string, unknown>;
  cid?: string;
};

export type TeamsMapGrouped = {
  version: number;
  data: Record<string, Record<string, TeamCompressedData>>;
};

/**
 * Generates lookup keys for a team (name, shortName, abbreviation, alternateNames, etc.)
 */
function getTeamLookupKeys(league: string, team: TeamRawData & { league: string }): string[] {
  const keys: string[] = [];
  
  try {
    if (team.name && typeof team.name === "string") {
      keys.push(team.name.toLowerCase());
    }
    if (team.shortName && typeof team.shortName === "string" && team.name?.toLowerCase() !== team.shortName.toLowerCase()) {
      keys.push(team.shortName.toLowerCase());
    }
    if (team.abbreviation && typeof team.abbreviation === "string") {
      keys.push(`${league.toLowerCase()}-${team.abbreviation.toLowerCase()}`);
    }
    if (league === "college" && team.city && typeof team.city === "string") {
      keys.push(`${league.toLowerCase()}-${team.city.toLowerCase()}`);
    }
    if (league === "rugby" && team.name && typeof team.name === "string") {
      keys.push(`${league.toLowerCase()}-${team.name.toLowerCase()}`);
    }
    if (Array.isArray(team.alternateNames)) {
      for (const name of team.alternateNames) {
        if (typeof name === "string") keys.push(name.toLowerCase());
      }
    }
  } catch {
    // Return whatever keys we have
  }
  
  return keys;
}

/**
 * Parses raw teams.json data into a grouped-by-competitionType structure
 * Used by both StyleDictionary (build-time) and client (runtime)
 */
export function parseTeamsData(input: TeamsJsonInput): TeamsMapGrouped {
  console.log("CUI: Parsing teams.json data");
  const result: TeamsMapGrouped = {
    version: typeof input?.version === "number" ? input.version : 1,
    data: {}
  };

  // Guard: invalid input
  if (!input || typeof input !== "object" || !input.teams || typeof input.teams !== "object") {
    return result;
  }

  try {
    const teamsEntries = Object.entries(input.teams);

    for (const [league, teams] of teamsEntries) {
      if (!league || typeof league !== "string" || !teams || typeof teams !== "object") continue;
      
      result.data[league] = {};
      
      for (const [id, team] of Object.entries(teams)) {
        if (!team || typeof team !== "object" || !team.name) continue;
        
        try {
          const teamWithLeague = { ...team, league };
          const compressedTeam = replaceKeys(teamWithLeague, keyMap) as TeamCompressedData;
          const lookupKeys = getTeamLookupKeys(league, teamWithLeague);
          
          for (const key of lookupKeys) {
            if (key) result.data[league][key] = compressedTeam;
          }
        } catch {
          // Skip this team if transformation fails
          continue;
        }
      }
    }
  } catch {
    // Return whatever we have
  }

  return result;
}

/**
 * Gets the list of competitionType keys from a parsed teams map
 */
export function getCompetitionTypeKeys(teamsMap: TeamsMapGrouped): string[] {
  if (!teamsMap || typeof teamsMap !== "object" || !teamsMap.data || typeof teamsMap.data !== "object") {
    return [];
  }
  try {
    return Object.keys(teamsMap.data);
  } catch {
    return [];
  }
}
